import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Building2, Plus, MapPin, ArrowRight, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

export default function Properties() {
  const { user } = useAuth();
  const [props, setProps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", address: "" });
  const nav = useNavigate();
  const canEdit = user?.role !== "viewer";

  const load = () => api.get("/properties").then((r) => setProps(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/properties", form);
      setForm({ name: "", address: "" });
      setOpen(false);
      toast.success("Property added");
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this property and its units?")) return;
    await api.delete(`/properties/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-6" data-testid="properties-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold uppercase tracking-tight text-slate-900">Properties</h1>
          <p className="text-sm text-slate-500">Set up buildings and their units</p>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="add-property-btn" className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow hover:bg-blue-700">
                <Plus className="h-4 w-4" /> Add Property
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display uppercase">New Property</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700">Property name</label>
                  <input data-testid="property-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Apex Heights" className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700">Address</label>
                  <input data-testid="property-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder="1200 Riverside Dr, Austin, TX" className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600" />
                </div>
                <DialogFooter>
                  <button data-testid="save-property-btn" className="w-full rounded-lg bg-blue-600 px-4 py-3 font-semibold text-white hover:bg-blue-700">Save property</button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {props.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center">
          <Building2 className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">No properties yet.</p>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {props.map((p) => (
          <div key={p.id} data-testid={`property-card-${p.id}`} onClick={() => nav(`/app/properties/${p.id}`)}
            className="group cursor-pointer rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:border-blue-300 hover:shadow-md">
            <div className="flex items-start justify-between">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><Building2 className="h-5 w-5" /></div>
              {canEdit && <button data-testid={`delete-property-${p.id}`} onClick={(e) => remove(p.id, e)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
            </div>
            <h3 className="mt-4 font-display text-xl font-bold text-slate-900">{p.name}</h3>
            <p className="mt-1 flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" />{p.address || "No address"}</p>
            <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
              <span className="text-sm font-semibold text-slate-700">{p.unit_count} unit{p.unit_count !== 1 ? "s" : ""}</span>
              <ArrowRight className="h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-1" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
